import { Flex, Input, Modal, Select, Space, Switch, Typography } from "antd"
import IconAndTextButton from "../IconAndTextButton"
import { faEnvelope, faLeft, faWarning } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import VSpace from "../common/VSpace"
import { useEmailTemplatesQuery } from "../EmailTemplates/emailTemplateQueries"
import { useEffect, useState } from "react"
import RichTextEditor from "../RichTextEditor/RichTextEditor"
import { defaultEmailTemplate } from "./proposalsModel"
import { EmailTemplate } from "../EmailTemplates/emailTemplateModel"
import { Tokens } from "../RichTextEditor/tokenModel"
import FollowUpSchedule from "../jobs/Proposals/Followup/FollowUpSchedule"
import { red } from "@ant-design/colors"
import { EmailTemplateTypes } from '../../common/constants'
import { useResendProposalMutation } from "./proposalQueries"
import { ProposalJobData } from "../jobs/Proposals/proposalsModel"
import { AutomationSchedule } from "../../domain/proposals/settings/proposalSettings"
import { useProposalSettingsQuery } from "../../domain/proposals/settings/proposalSettingsQueries"

export type Props = {
  job: ProposalJobData
  proposalId: number
  proposalTotal: number
  isOpen: boolean
  onClose: () => void
}

const SendProposalModal = ({ job, proposalId, proposalTotal, isOpen = false, onClose }: Props) => {
  const salesPersonEmail = job.salesPersonEmail
  const customerEmails = job.customer?.emails ?? []

  const proposalSettingsQuery = useProposalSettingsQuery()
  const emailTemplatesQuery = useEmailTemplatesQuery()
  const [selectedCustomerEmails, setSelectedCustomerEmails] = useState<string[]>([])
  const [addReplyToAddress, setAddReplyToAddress] = useState(true)
  const [replyToAddresses, setReplyToAddresses] = useState<string>(salesPersonEmail ?? '')
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplate>(defaultEmailTemplate)
  const [emailSubject, setEmailSubject] = useState<string>(defaultEmailTemplate.emailSubject ?? '')
  const [emailBody, setEmailBody] = useState<string>(defaultEmailTemplate.emailBody ?? '')
  const validateEmailBody = (body: string) => {
    return body.toLowerCase().includes('{{proposal_link}}')
  }
  const [emailBodyValidation, setEmailBodyValidation] = useState(validateEmailBody(emailBody))
  const [sendPDF, setSendPdf] = useState(false)
  useEffect(() => {
    if (proposalSettingsQuery.data) {
      setSendPdf(proposalSettingsQuery.data.sendProposalPdfByDefault)
    }
  }, [proposalSettingsQuery.data])
  const [notifySalesPerson, setNotifySalesPerson] = useState(true)
  const [automatedSchedule, setAutomatedSchedule] = useState<AutomationSchedule | null>(null)
  const resendMutation = useResendProposalMutation(() => {
    if (window && window.Intercom) {
      const metadata = {
        job_name: job.name,
        date: job.estimateDate,
        salesperson: job.salesPersonName,
        customer: job.customer?.displayName ?? '',
        estimated_price: (proposalTotal / 100).toFixed(2)
      };
      window.Intercom("trackEvent", "sent-proposal", metadata);
    }
    onClose()
  })

  const toggleCustomerEmail = (email: string) => {
    if (selectedCustomerEmails.includes(email)) {
      setSelectedCustomerEmails(x => x.filter(x => x !== email))
    } else {
      setSelectedCustomerEmails(x => [...x, email])
    }
  }

  const emailTemplates = [
    defaultEmailTemplate,
    ...emailTemplatesQuery.data ?? []
  ]

  const emailTemplateOptions = emailTemplates.filter(x => x.type === EmailTemplateTypes.Proposal).map(e => ({
    label: e.name,
    value: e.guid
  }))

  const resetModal = () => {
    setAddReplyToAddress(false)
    setSelectedCustomerEmails([])
    setReplyToAddresses(salesPersonEmail ?? '')
    setSelectedEmailTemplate(defaultEmailTemplate)
    setEmailSubject(defaultEmailTemplate.emailSubject ?? '')
    setEmailBody(defaultEmailTemplate.emailBody ?? '')
    setSendPdf(proposalSettingsQuery.data?.sendProposalPdfByDefault ?? false)
    setNotifySalesPerson(true)
    setAutomatedSchedule(null)
    setEmailBodyValidation(validateEmailBody(defaultEmailTemplate.emailBody ?? ''))
  }

  useEffect(() => {
    if (salesPersonEmail !== undefined) {
      setReplyToAddresses(salesPersonEmail)
    }
  }, [salesPersonEmail])

  return <Modal 
    title={<Space><FontAwesomeIcon icon={faEnvelope} />Submit Customer Proposal For Approval</Space>}
    open={isOpen}
    width={900}
    afterClose={resetModal}
    onCancel={onClose}
    footer={[
      <IconAndTextButton
        key="cancel"
        onClick={onClose}
        icon={faLeft}
        text="Cancel"
        disabled={resendMutation.isLoading}
      />,
      <IconAndTextButton
        type="primary"
        key="print"
        onClick={() => {
          resendMutation.mutate({
            projectId: job.id,
            proposalId,
            recipients: selectedCustomerEmails.map(x => ({ address: x })),
            emailReplyToAddresses: replyToAddresses ? replyToAddresses.split(',').map(x => ({ address: x})) : [],
            emailSubject,
            emailBody,
            sendPdf: sendPDF,
            notifySalesPersonOfCustomerViews: notifySalesPerson,
            followUpAutomation: automatedSchedule?.enabled ? automatedSchedule : null
          })
        }}
        loading={resendMutation.isLoading}
        disabled={selectedCustomerEmails.length < 1 || !addReplyToAddress || resendMutation.isLoading}
        icon={faEnvelope}
        text="Send for Review"
      />,
    ]}
  >
    <VSpace size="large">
      <VSpace>
        Select the recipients of the proposal.
        <Flex gap={16} wrap="wrap">
         {customerEmails.map(customer => (
            <Space key={customer.address}>
              <Switch value={selectedCustomerEmails.includes(customer.address)} onChange={() => toggleCustomerEmail(customer.address)} />
              {customer.address} {customer.isPrimary && <b>(primary)</b>}
            </Space>
          ))}
        </Flex>
        {selectedCustomerEmails.length === 0 && <Typography.Text type="danger">At least one recipient must be selected</Typography.Text>}
      </VSpace>

      <VSpace>
        Select the reply to email addresses of the proposal.
        <Space>
          <Switch 
            value={addReplyToAddress} 
            onChange={ e =>{ 
              if(replyToAddresses.length < 1){
                setReplyToAddresses(salesPersonEmail)
              }
              setAddReplyToAddress(e)}} />
          <Input 
            value={replyToAddresses} 
            onChange={e => {
              setReplyToAddresses(e.target.value)}} 
          />
        </Space>
        {!addReplyToAddress && <Typography.Text type="danger">At least one reply to address must be selected</Typography.Text>}
      </VSpace>

      <VSpace>
        Email Template
        <Select 
          options={emailTemplateOptions} 
          value={selectedEmailTemplate.guid}  
          style={{ width: '100%' }} 
          onSelect={ (selected) => {
            const template =  emailTemplates.find( x => x.guid === selected )
            setSelectedEmailTemplate(template ?? defaultEmailTemplate)
            setEmailBody(template?.emailBody ?? '')
            setEmailSubject(template?.emailSubject ?? '')
            setEmailBodyValidation(validateEmailBody(template?.emailBody ?? ''))
          }} />
      </VSpace>

      <VSpace>
        Email Subject
        <RichTextEditor value={emailSubject} onChange={setEmailSubject} singleLine tokens={Tokens} />
      </VSpace>

      <VSpace>
        Email Body
        <RichTextEditor 
          value={emailBody} 
          tokens={Tokens}
          onChange={(newValue)=> {
            setEmailBody(newValue)
            setEmailBodyValidation(validateEmailBody(newValue))} } />
          {!emailBodyValidation &&
            <div>
              <FontAwesomeIcon icon={faWarning} color={red.primary} />
              <span>{`The email body must contain {{proposal_link}} text at least once.`}</span>
            </div>
          }
      </VSpace>

      <VSpace>
        <Space>
          <Switch 
            value={sendPDF}
            onChange={(value)=> setSendPdf(value)} />
            Allow the customer to download a PDF copy of the proposal.
          </Space>
      </VSpace>

      <VSpace>
        <Space>
          <Switch 
            disabled={salesPersonEmail === undefined} 
            value={notifySalesPerson}
            onChange={(value)=> setNotifySalesPerson(value)} />
            Notify the sales person when the customer has viewed the proposal.
          </Space>
      </VSpace>

      <VSpace>
        <FollowUpSchedule
          onScheduleChange={(errors, schedule)=>{ if (!errors) {
            setAutomatedSchedule(schedule)}
          }}
          showDates={true}
        />
      </VSpace>

    </VSpace>
  </Modal>
}

export default SendProposalModal
